/**
 * CloudWatch Logger Service
 * 
 * Centralized logging service that sends frontend logs to CloudWatch via KatalLogger.
 * Particularly useful for debugging issues on mobile devices where console access is limited.
 * 
 * Features:
 * - Automatic device/browser context enrichment
 * - Log batching for performance
 * - Offline queue with retry logic
 * - Production-only logging (uses console in development)
 * - User and session context tracking
 * 
 * ============================================
 * HOW TO SEARCH LOGS IN CLOUDWATCH INSIGHTS:
 * ============================================
 * 
 * Basic query (shows just the log messages):
 * ```
 * fields katalLogger.message
 * | sort @timestamp desc
 * ```
 * 
 * Search for specific user:
 * ```
 * fields katalLogger.message
 * | filter katalLogger.message like /useralias/
 * | sort @timestamp desc
 * ```
 * 
 * Search for mobile logs:
 * ```
 * fields katalLogger.message
 * | filter katalLogger.message like /\[mobile\]/
 * | sort @timestamp desc
 * ```
 * 
 * Search for errors:
 * ```
 * fields katalLogger.message
 * | filter katalLogger.level = "error"
 * | sort @timestamp desc
 * ```
 * 
 * Search for specific text:
 * ```
 * fields katalLogger.message
 * | filter katalLogger.message like /Camera/
 * | sort @timestamp desc
 * ```
 */

import KatalLogger from '@amzn/katal-logger';
import { deviceDetectionService } from './deviceDetectionService';

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

export interface LogContext {
  [key: string]: any;
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  context?: LogContext;
  timestamp: string;
  error?: {
    name?: string;
    message?: string;
    stack?: string;
  };
}


class CloudWatchLoggerService {
  private katalLogger: KatalLogger | null = null;
  private isInitialized = false;
  private isDevelopment = process.env.NODE_ENV !== 'production';
  private logQueue: LogEntry[] = [];
  private batchSize = 10;
  private batchTimeout = 5000; // 5 seconds
  private batchTimer: NodeJS.Timeout | null = null;
  private sessionId: string;
  private userId: string | null = null;
  private cachedDeviceType: 'mobile' | 'desktop' | null = null;

  constructor() {
    this.sessionId = this.generateSessionId();
    
    // Listen for online/offline events to manage queue
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.flushQueue());
    }
  }

  /**
   * Initialize the logger with the LoggerUrl from CDK deployment
   * This should be called once during app initialization
   */
  initialize(loggerUrl: string, userId?: string): void {
    if (this.isInitialized) {
      console.warn('[CloudWatchLogger] Already initialized');
      return;
    }

    if (!loggerUrl) {
      console.warn('[CloudWatchLogger] No LoggerUrl provided, logging disabled');
      return;
    }

    try {
      this.katalLogger = new KatalLogger({ url: loggerUrl });
      this.isInitialized = true;
      this.userId = userId || null;
      
      // Cache device type once to avoid repeated calls and prevent infinite loops
      this.cachedDeviceType = deviceDetectionService.getDeviceType();
      
      if (!this.isDevelopment) {
        console.log('[CloudWatchLogger] Initialized with URL:', loggerUrl);
        // Log successful initialization
        this.info('CloudWatch logger initialized', {
          sessionId: this.sessionId,
          userId: this.userId
        });
      }
    } catch (error) {
      console.error('[CloudWatchLogger] Failed to initialize:', error);
    }
  }

  /**
   * Set the current user ID for log context
   */
  setUserId(userId: string): void {
    this.userId = userId;
  }

  /**
   * Log a debug message (only in development)
   */
  debug(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.log(`[DEBUG] ${message}`, context);
    }
    // Don't send debug logs to CloudWatch to reduce noise
  }

  /**
   * Log an info message
   */
  info(message: string, context?: LogContext): void {
    this.log('INFO', message, context);
  }

  /**
   * Log a warning message
   */
  warn(message: string, context?: LogContext): void {
    this.log('WARN', message, context);
  }

  /**
   * Log an error message
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    const errorInfo = this.extractErrorInfo(error);
    this.log('ERROR', message, { ...context, error: errorInfo });
  }

  /**
   * Core logging method that handles all log levels
   */
  private log(level: LogLevel, message: string, context?: LogContext): void {
    const logEntry: LogEntry = {
      level,
      message,
      context,
      timestamp: new Date().toISOString()
    };

    // Always log to console in development
    if (this.isDevelopment) {
      this.logToConsole(level, message, context);
      return;
    }

    // In production, add to queue
    if (this.isInitialized && this.katalLogger) {
      this.logQueue.push(logEntry);
      
      // Flush immediately for errors, otherwise batch
      if (level === 'ERROR') {
        this.flushQueue();
      } else {
        this.scheduleBatchFlush();
      }
    } else {
      // Fallback to console if not initialized
      this.logToConsole(level, message, context);
    }
  }

  /**
   * Log to browser console with appropriate method
   */
  private logToConsole(level: LogLevel, message: string, context?: LogContext): void {
    const consoleMethod = level === 'ERROR' ? 'error' : level === 'WARN' ? 'warn' : 'log';
    console[consoleMethod](`[${level}] ${message}`, context || '');
  }

  /**
   * Schedule batch flush with debouncing
   */
  private scheduleBatchFlush(): void {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
    }

    if (this.logQueue.length >= this.batchSize) {
      this.flushQueue();
    } else {
      this.batchTimer = setTimeout(() => {
        this.flushQueue();
      }, this.batchTimeout);
    }
  }

  /**
   * Flush the log queue to CloudWatch
   */
  private flushQueue(): void {
    if (this.logQueue.length === 0 || !this.katalLogger) {
      return;
    }

    // Check if online
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      console.log('[CloudWatchLogger] Offline, queuing logs');
      return;
    }

    const logsToSend = [...this.logQueue];
    this.logQueue = [];

    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    // Send each log entry
    logsToSend.forEach(logEntry => {
      try {
        const katalLogMethod = this.getKatalLogMethod(logEntry.level);
        
        // Format message with prefix: [user] [device] [path] message
        const formattedMessage = this.formatMessage(logEntry.message);
        
        // For errors, append error details
        let finalMessage = formattedMessage;
        if (logEntry.level === 'ERROR' && logEntry.context?.error) {
          const err = logEntry.context.error;
          finalMessage += `\nError: ${err.name} - ${err.message}`;
          if (err.stack) {
            finalMessage += `\nStack: ${err.stack}`;
          }
        }
        
        // KatalLogger methods: info(message, error, context)
        // Pass simple formatted message, no complex context
        katalLogMethod.call(
          this.katalLogger,
          finalMessage,
          undefined, // no error object
          {} // empty context
        );
      } catch (error) {
        console.error('[CloudWatchLogger] Failed to send log:', error);
        // Don't re-queue to avoid infinite loops
      }
    });
  }

  /**
   * Get the appropriate KatalLogger method for log level
   */
  private getKatalLogMethod(level: LogLevel): Function {
    if (!this.katalLogger) {
      return () => {};
    }

    switch (level) {
      case 'ERROR':
        return this.katalLogger.error.bind(this.katalLogger);
      case 'WARN':
        return this.katalLogger.warn.bind(this.katalLogger);
      case 'INFO':
      case 'DEBUG':
      default:
        return this.katalLogger.info.bind(this.katalLogger);
    }
  }

  /**
   * Create simple log prefix: [user] [device] [path]
   */
  private createLogPrefix(): string {
    // userId is already the alias (extracted at the call site)
    const userAlias = this.userId || 'anonymous';
    
    // Use cached device type to avoid repeated calls and prevent infinite loops
    const deviceType = this.cachedDeviceType || 'unknown';
    
    // Get current path
    const path = window.location.pathname;
    
    return `[${userAlias}] [${deviceType}] [${path}]`;
  }

  /**
   * Format message with prefix for CloudWatch
   */
  private formatMessage(message: string): string {
    const prefix = this.createLogPrefix();
    return `${prefix} ${message}`;
  }

  /**
   * Extract error information from Error object or unknown type
   */
  private extractErrorInfo(error: Error | unknown): any {
    if (!error) {
      return undefined;
    }

    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack
      };
    }

    // Handle non-Error objects
    if (typeof error === 'object') {
      return {
        name: (error as any).name || 'Unknown',
        message: (error as any).message || JSON.stringify(error),
        stack: (error as any).stack
      };
    }

    return {
      message: String(error)
    };
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Force flush all queued logs (useful before page unload)
   */
  forceFlush(): void {
    this.flushQueue();
  }

  /**
   * Get logger status for debugging
   */
  getStatus(): {
    isInitialized: boolean;
    isDevelopment: boolean;
    queueSize: number;
    sessionId: string;
    userId: string | null;
  } {
    return {
      isInitialized: this.isInitialized,
      isDevelopment: this.isDevelopment,
      queueSize: this.logQueue.length,
      sessionId: this.sessionId,
      userId: this.userId
    };
  }
}

// Export singleton instance
export const cloudWatchLogger = new CloudWatchLoggerService();

export default cloudWatchLogger;
